import { AfterViewInit, Component, ElementRef, EventEmitter, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let turnstileScriptPromise: Promise<any> | null = null;

function loadTurnstile(): Promise<any> {
	if ((window as any).turnstile) {
		return Promise.resolve((window as any).turnstile);
	}
	if (!turnstileScriptPromise) {
		turnstileScriptPromise = new Promise((resolve, reject) => {
			const s = document.createElement('script');
			s.src = TURNSTILE_SCRIPT_SRC;
			s.async = true;
			s.onload = () => resolve((window as any).turnstile);
			s.onerror = () => {
				turnstileScriptPromise = null;
				s.remove();
				reject(new Error(`Failed to load: ${TURNSTILE_SCRIPT_SRC}`));
			};
			document.body.appendChild(s);
		});
	}
	return turnstileScriptPromise;
}

@Component({
	selector: 'app-turnstile',
	templateUrl: './turnstile.component.html',
	styleUrls: ['./turnstile.component.scss'],
})
export class TurnstileComponent implements AfterViewInit, OnDestroy {
	@ViewChild('container', { static: true }) container: ElementRef<HTMLDivElement>;
	@Output() tokenChange = new EventEmitter<string | null>();

	loadError = false;
	private widgetId: string | null = null;
	private destroyed = false;

	constructor(
		private zone: NgZone,
		private translate: TranslateService
	) {}

	async ngAfterViewInit() {
		try {
			const turnstile = await loadTurnstile();
			if (this.destroyed) {
				return;
			}
			this.widgetId = turnstile.render(this.container.nativeElement, {
				sitekey: environment.turnstileSiteKey,
				language: this.translate.currentLang || 'auto',
				callback: (token: string) => this.emit(token),
				'expired-callback': () => this.emit(null),
				'error-callback': () => this.emit(null),
			});
		} catch (e) {
			console.error(e);
			this.loadError = true;
		}
	}

	reset() {
		if (this.widgetId !== null) {
			(window as any).turnstile?.reset(this.widgetId);
		}
		this.emit(null);
	}

	ngOnDestroy() {
		this.destroyed = true;
		if (this.widgetId !== null) {
			(window as any).turnstile?.remove(this.widgetId);
			this.widgetId = null;
		}
	}

	private emit(token: string | null) {
		this.zone.run(() => this.tokenChange.emit(token));
	}
}
